import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Users, Shield, Loader2 } from 'lucide-react';

interface TeamMember {
  id: number;
  email: string;
  displayName: string;
  role: string;
  roleDisplayName: string;
}

const ROLE_OPTIONS = [
  { value: 'AGENCY_MANAGER', label: 'Manager' },
  { value: 'ADVISOR', label: 'Associate Advisor' },
] as const;

const ROLE_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  AGENCY_OWNER: 'default',
  AGENCY_MANAGER: 'secondary',
  ADVISOR: 'outline',
};

export default function TeamManagement() {
  const { toast } = useToast();
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery<{ members: TeamMember[] }>({
    queryKey: ['/api/team/members'],
    enabled: !!user,
  });

  const changeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: string }) => {
      return apiRequest('PATCH', `/api/team/members/${userId}/role`, { role });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/team/members'] });
      toast({
        title: 'Role updated',
        description: `Team member role changed to ${ROLE_OPTIONS.find((r) => r.value === variables.role)?.label ?? variables.role}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update role',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      });
    },
  });

  const members = data?.members ?? [];
  const isOwnerRow = (member: TeamMember) => member.role === 'AGENCY_OWNER';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load team members. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Users className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team Management</h1>
          <p className="text-muted-foreground">
            Manage your agency team members and their roles
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Team Members
          </CardTitle>
          <CardDescription>
            Assign roles to control what each team member can see and do.
            Managers see all agency data; Associate Advisors see only their own.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No team members found</p>
              <p className="text-sm mt-1">
                Team members will appear here once they are assigned to your agency.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{member.displayName}</p>
                      <Badge variant={ROLE_BADGE_VARIANT[member.role] ?? 'outline'}>
                        {member.roleDisplayName}
                      </Badge>
                      {member.id === user?.id && (
                        <Badge variant="outline" className="text-xs">You</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {member.email}
                    </p>
                  </div>

                  <div className="ml-4 w-48 shrink-0">
                    {isOwnerRow(member) ? (
                      <div className="text-sm text-muted-foreground text-right">
                        Owner (cannot change)
                      </div>
                    ) : (
                      <Select
                        value={member.role}
                        onValueChange={(value) =>
                          changeRoleMutation.mutate({
                            userId: member.id,
                            role: value,
                          })
                        }
                        disabled={changeRoleMutation.isPending || member.id === user?.id}
                      >
                        <SelectTrigger aria-label={`Role for ${member.displayName}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* AR-210 (TC-22.2): a role outside the assignable options (e.g.
                              System Admin) left the trigger blank. Show it as the current,
                              non-selectable value. */}
                          {!ROLE_OPTIONS.some((opt) => opt.value === member.role) && (
                            <SelectItem value={member.role} disabled>
                              {member.roleDisplayName}
                            </SelectItem>
                          )}
                          {ROLE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
